import {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import styles from "./AppShell.module.css";
import { useMediaQuery } from "./useMediaQuery";

const DEFAULT_BREAKPOINT = 740;

export interface AppShellProps {
  brand: ReactNode;
  sidebar: ReactNode;
  children: ReactNode;
  /**
   * Persistent status line (the sync indicator). Pinned to the top of the
   * main column, where a degraded state has the width to say what is wrong —
   * the sidebar is too narrow to hold the full wording. Centred over the
   * content column rather than the scrollport, so it stays on the same axis
   * as what it sits above however wide the monitor gets.
   */
  status?: ReactNode;
  breakpoint?: number;
  hamburgerLabel?: string;
  closeLabel?: string;
  sidebarTestId?: string;
  hamburgerTestId?: string;
  mobileTopBarTestId?: string;
  drawerScrimTestId?: string;
  drawerCloseTestId?: string;
}

export function AppShell({
  brand,
  sidebar,
  children,
  status,
  breakpoint = DEFAULT_BREAKPOINT,
  hamburgerLabel = "Åpne meny",
  closeLabel = "Lukk meny",
  sidebarTestId = "nav-drawer",
  hamburgerTestId = "hamburger-button",
  mobileTopBarTestId = "mobile-top-bar",
  drawerScrimTestId = "drawer-scrim",
  drawerCloseTestId = "drawer-close-button",
}: AppShellProps) {
  const isDesktop = useMediaQuery(`(min-width: ${breakpoint}px)`);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const close = useCallback(() => setDrawerOpen(false), []);
  const open = useCallback(() => setDrawerOpen(true), []);

  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setDrawerOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  function handleSidebarClick(event: MouseEvent<HTMLElement>) {
    if (isDesktop || !drawerOpen) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest("a")) close();
  }

  const mode = isDesktop ? "desktop" : "drawer";

  return (
    <div
      className={styles.shell}
      data-mode={mode}
      data-drawer-open={!isDesktop && drawerOpen ? "true" : undefined}
    >
      {!isDesktop && (
        <header
          className={styles.mobileTopBar}
          data-testid={mobileTopBarTestId}
        >
          {brand}
          <button
            type="button"
            className={styles.hamburger}
            aria-label={hamburgerLabel}
            onClick={open}
            data-testid={hamburgerTestId}
          >
            <HamburgerIcon />
          </button>
        </header>
      )}
      <aside
        className={styles.sidebar}
        data-testid={sidebarTestId}
        data-drawer-open={!isDesktop && drawerOpen ? "true" : undefined}
        onClick={handleSidebarClick}
      >
        {!isDesktop && (
          <button
            type="button"
            className={styles.drawerClose}
            aria-label={closeLabel}
            onClick={close}
            data-testid={drawerCloseTestId}
          >
            <CloseIcon />
          </button>
        )}
        {isDesktop && <div className={styles.brandWrap}>{brand}</div>}
        {sidebar}
      </aside>
      {!isDesktop && drawerOpen && (
        <button
          type="button"
          className={styles.scrim}
          aria-label={closeLabel}
          onClick={close}
          data-testid={drawerScrimTestId}
        />
      )}
      <main className={styles.main}>
        {status && <div className={styles.statusWrap}>{status}</div>}
        <div className={styles.mainColumn}>{children}</div>
      </main>
    </div>
  );
}

function HamburgerIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
